import React, { useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import type { FeedbackType } from '@/types/chatbot';
import { useTranslation } from 'react-i18next';

interface FeedbackDetailsDialogProps {
  isOpen: boolean;
  feedbackType: FeedbackType | null;
  onSubmitDetails: (details: string) => Promise<void>;
  onClose: () => void;
}

export const FeedbackDetailsDialog: React.FC<FeedbackDetailsDialogProps> = ({
  isOpen,
  feedbackType,
  onSubmitDetails,
  onClose
}) => {
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await onSubmitDetails(details.trim());
      setDetails('');
      onClose();
    } catch (error) {
      console.error('Error submitting details:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setDetails('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t('chatFeedback.add_details_title')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {feedbackType === 'positive' 
              ? t('chatFeedback.add_details_prompt_positive') 
              : t('chatFeedback.add_details_prompt_negative')}
          </p>
          
          <Textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder={t('chatFeedback.details_placeholder')}
            className="min-h-[100px] resize-none"
            maxLength={500}
          />
          <p className="text-xs text-gray-500">
            {t('chatFeedback.characters_count', { count: details.length })}
          </p>
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1"
          >
            {t('chatFeedback.skip')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !details.trim()}
            className="flex-1"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                {t('chatFeedback.submitting')}
              </>
            ) : (
              <>
                <Send className="w-3.5 h-3.5 mr-2" />
                {t('chatFeedback.add_details')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};